import { useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Package, LogOut, Download, Menu, X, Search, ChevronDown, Check, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { NAV_GROUPS, itemAllowed } from "@/lib/nav";
import { THEMES, useTheme } from "@/lib/uiPrefs";
import NotificationBell from "@/components/NotificationBell";

function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  return (
    <div className="px-3 py-2" data-testid="theme-switcher">
      <div className="flex items-center gap-1.5 mb-1.5 text-[10px] uppercase tracking-widest font-mono" style={{ color: "var(--sidebar-group)" }}>
        <Palette className="w-3 h-3" /> Color tone
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {THEMES.map((t) => {
          const active = theme === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTheme(t.id)}
              aria-pressed={active}
              aria-label={`${t.label} color tone${active ? " (selected)" : ""}`}
              data-testid={`theme-option-${t.id}`}
              className="flex flex-col items-center gap-1 rounded-md px-1.5 py-1.5 text-[10px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
              style={{
                color: "var(--sidebar-fg)",
                background: active ? "var(--sidebar-active-bg)" : "transparent",
                boxShadow: active ? "inset 0 0 0 1px var(--sidebar-active-fg)" : "inset 0 0 0 1px var(--sidebar-border)",
              }}
            >
              <span className="relative inline-grid place-items-center w-4 h-4 rounded-full" style={{ background: t.swatch }}>
                {active && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
              </span>
              <span className="leading-tight text-center">{t.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const doExport = () => {
    const token = localStorage.getItem("token");
    const url = `${API}/export/inventory.xlsx?auth=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank");
  };

  const showBell = can("notification:view") || can("task:view_own") || can("task:manage") || user?.all_access;

  // Permitted items only, then filtered by the local label search (no data/PII, no backend).
  const q = query.trim().toLowerCase();
  const groups = useMemo(() => {
    return NAV_GROUPS.map((g) => {
      const items = g.items
        .filter((it) => itemAllowed(it, can))
        .filter((it) => (q ? it.label.toLowerCase().includes(q) : true));
      return { ...g, items };
    }).filter((g) => g.items.length > 0);
  }, [can, q]);

  const toggleGroup = (label) => setCollapsed((c) => ({ ...c, [label]: !c[label] }));

  const nav = (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-3 pt-3 pb-2">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--sidebar-muted)" }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search menu…"
            data-testid="nav-search"
            aria-label="Search menu"
            className="w-full rounded-md pl-8 pr-7 py-2 text-sm bg-transparent focus:outline-none focus-visible:ring-2"
            style={{ color: "var(--sidebar-fg)", boxShadow: "inset 0 0 0 1px var(--sidebar-border)" }}
          />
          {query && (
            <button onClick={() => setQuery("")} data-testid="nav-search-clear" aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded" style={{ color: "var(--sidebar-muted)" }}>
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <nav className="flex-1 px-3 pb-3 space-y-1.5 overflow-y-auto" data-testid="sidebar-nav">
        {groups.length === 0 && (
          <div className="px-3 py-4 text-sm" style={{ color: "var(--sidebar-muted)" }} data-testid="nav-no-results">
            No matching menu items.
          </div>
        )}
        {groups.map((g) => {
          const isCollapsed = q ? false : !!collapsed[g.label];
          return (
            <div key={g.label} data-testid={`nav-group-${g.label.toLowerCase()}`}>
              <button
                type="button"
                onClick={() => toggleGroup(g.label)}
                disabled={!!q}
                data-testid={`nav-group-toggle-${g.label.toLowerCase()}`}
                className="w-full flex items-center justify-between px-3 py-1 text-[10px] uppercase tracking-widest font-mono transition-colors"
                style={{ color: "var(--sidebar-group)" }}
              >
                <span>{g.label}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
              </button>
              {!isCollapsed && (
                <div className="mt-0.5 space-y-0.5">
                  {g.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink key={item.to} to={item.to} data-testid={item.testid} onClick={() => setOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors"
                        style={({ isActive }) => ({
                          color: isActive ? "var(--sidebar-active-fg)" : "var(--sidebar-fg)",
                          background: isActive ? "var(--sidebar-active-bg)" : "transparent",
                          fontWeight: isActive ? 600 : 400,
                        })}
                        onMouseEnter={(e) => { if (!e.currentTarget.getAttribute("aria-current")) e.currentTarget.style.background = "var(--sidebar-hover)"; }}
                        onMouseLeave={(e) => { if (!e.currentTarget.getAttribute("aria-current")) e.currentTarget.style.background = "transparent"; }}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );

  const footer = (
    <div style={{ borderTop: "1px solid var(--sidebar-border)" }}>
      <ThemeSwitcher />
      <div className="px-3 pb-3">
        {can("finance:view") && (
          <Button variant="ghost" onClick={doExport} data-testid="export-excel-button"
            className="w-full justify-start mb-2 hover:bg-[var(--sidebar-hover)]" style={{ color: "var(--sidebar-fg)" }}>
            <Download className="w-4 h-4 mr-2" />Export Excel
          </Button>
        )}
        <div className="px-3 py-2 text-xs">
          <div style={{ color: "var(--sidebar-muted)" }}>Signed in as</div>
          <div className="font-medium truncate" data-testid="user-email" style={{ color: "var(--sidebar-fg)" }}>{user?.email}</div>
          <div className="mt-1 inline-block px-2 py-0.5 rounded uppercase text-[10px] tracking-widest font-mono" data-testid="user-role"
            style={{ background: "var(--sidebar-hover)", color: "var(--sidebar-muted)" }}>
            {user?.role}
          </div>
        </div>
        <Button variant="ghost" onClick={async () => { await logout(); navigate("/login"); }}
          data-testid="logout-button"
          className="w-full justify-start hover:bg-[var(--sidebar-hover)]" style={{ color: "var(--sidebar-fg)" }}>
          <LogOut className="w-4 h-4 mr-2" />Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen md:flex">
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-30 flex items-center justify-between px-3 h-14" data-testid="mobile-topbar"
        style={{ background: "var(--sidebar-bg)", color: "var(--sidebar-fg)", borderBottom: "1px solid var(--sidebar-border)" }}>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" className="p-2 rounded-md hover:bg-[var(--sidebar-hover)]">
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md grid place-items-center" style={{ background: "var(--sidebar-brand)", color: "var(--sidebar-brand-fg)" }}><Package className="w-4 h-4" /></div>
          <span className="font-heading font-bold text-sm">CargoLedger</span>
        </div>
        {showBell ? <NotificationBell /> : <span className="w-9" />}
      </div>

      {/* Backdrop (mobile) */}
      {open && <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setOpen(false)} data-testid="sidebar-backdrop" />}

      {/* Sidebar */}
      <aside data-testid="sidebar"
        style={{ background: "var(--sidebar-bg)", color: "var(--sidebar-fg)" }}
        className={`flex flex-col w-64 flex-shrink-0 fixed md:static inset-y-0 left-0 z-50 transform transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}>
        <div className="p-5 flex items-center gap-2.5" style={{ borderBottom: "1px solid var(--sidebar-border)" }}>
          <div className="w-9 h-9 rounded-md grid place-items-center" style={{ background: "var(--sidebar-brand)", color: "var(--sidebar-brand-fg)" }}>
            <Package className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-bold text-sm">CargoLedger</div>
            <div className="caption text-[10px]" style={{ color: "var(--sidebar-muted)" }}>CN → MY</div>
          </div>
          <div className="hidden md:block">{showBell && <NotificationBell />}</div>
          <button className="md:hidden p-1.5 rounded hover:bg-[var(--sidebar-hover)]" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        {nav}
        {footer}
      </aside>

      <main className="flex-1 overflow-x-auto min-w-0">
        <div className="max-w-[1600px] mx-auto p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
